import { Component, AfterViewInit, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgClass, NgFor } from '@angular/common';
import { UserCategory } from '../../../../../map/user-guide/user-guide.service';
import { CATEGORY_CHIPS, CategoryChip } from './menu-preset.config';
import { GoogleAnalyticsService } from '../../../../../user/google-analytics.service';

@Component({
  selector: 'app-menu-preset-chips',
  standalone: true,
  imports: [NgFor, NgClass],
  template: `
    <div class="preset-chips-wrapper" [class.has-overflow]="hasOverflow">
      <div class="preset-chips-row" #chipsRow
           (wheel)="onWheel($event)"
           (scroll)="updateOverflow()"
           (mousedown)="onDragStart($event)"
           (mousemove)="onDragMove($event)"
           (mouseup)="onDragEnd()"
           (mouseleave)="onDragEnd()">
        <button
          *ngFor="let chip of chips"
          class="preset-chip"
          [ngClass]="{ active: activePreset === chip.id }"
          (click)="onChipClick(chip)"
        >
          {{ chip.label }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .preset-chips-wrapper {
      position: relative;
      width: 100%;
      margin-right: -38px;
    }

    .preset-chips-wrapper.has-overflow::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 48px;
      background: linear-gradient(to right, transparent, white);
      pointer-events: none;
    }

    .preset-chips-row {
      display: flex;
      flex-direction: row;
      gap: 8px;
      overflow-x: auto;
      scrollbar-width: none;
      box-sizing: border-box;
      width: 100%;
      padding: 2px 8px 2px 32px;
      flex-shrink: 0;
    }

    .preset-chips-row::-webkit-scrollbar {
      display: none;
    }

    .preset-chip {
      flex-shrink: 0;
      height: 38px;
      padding: 0 16px;
      border-radius: 12px;
      border: none;
      background-color: #F1F1F1;
      color: var(--typography-gray, #888);
      font-family: 'Manrope', sans-serif;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.15s, color 0.15s;
      user-select: none;
      white-space: nowrap;

      &.active {
        background-color: #02A4FF1A;
        color: var(--primary-blue, #02A4FF);
        font-weight: 600;
      }

      &:hover:not(.active) {
        background-color: #E5E5E5;
      }
    }
  `],
})
export class MenuPresetChipsComponent implements AfterViewInit {
  @Input() activePreset: UserCategory | null = null;
  @Output() presetChange = new EventEmitter<UserCategory>();
  @ViewChild('chipsRow') chipsRow!: ElementRef<HTMLElement>;

  readonly chips: CategoryChip[] = CATEGORY_CHIPS;
  hasOverflow = false;

  constructor(private ga: GoogleAnalyticsService) {}

  ngAfterViewInit(): void {
    this.updateOverflow();
  }

  onChipClick(chip: CategoryChip): void {
    const isDeselect = this.activePreset === chip.id;
    if (!isDeselect) {
      this.ga.logUserChangedCategory(chip.id);
    }
    this.presetChange.emit(isDeselect ? 'unspecified' : chip.id);
  }

  private dragStartX = 0;
  private dragScrollLeft = 0;
  private isDragging = false;

  onDragStart(e: MouseEvent): void {
    this.isDragging = true;
    this.dragStartX = e.pageX;
    this.dragScrollLeft = this.chipsRow.nativeElement.scrollLeft;
  }

  onDragMove(e: MouseEvent): void {
    if (!this.isDragging) return;
    e.preventDefault();
    this.chipsRow.nativeElement.scrollLeft = this.dragScrollLeft - (e.pageX - this.dragStartX);
  }

  onDragEnd(): void {
    this.isDragging = false;
  }

  onWheel(e: WheelEvent): void {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    if (e.deltaY === 0) return;
    e.preventDefault();
    this.chipsRow.nativeElement.scrollBy({ left: e.deltaY, behavior: 'auto' });
  }

  updateOverflow(): void {
    const el = this.chipsRow.nativeElement;
    this.hasOverflow = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }
}
